import * as React from 'react';
import { saveAs } from 'file-saver';

// Material-UI
import { CloudDownload } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid';
import Tooltip from '@mui/material/Tooltip';

// Styles
import { useTheme } from '@mui/material/styles';

// Components
import FileIcon from './FileIcon';

import {
  API_URL,
  GDFileType,
  RESOURCE_BREADCRUMB_ROOT_ID
} from '../global';

import {
  GDFile,
  HTTPHeaders,
  ResourceBreadcrumb
} from "../interfaces";

// Auth0 user hooks
import { useAuth0User } from '../hooks/UseAuth0User';

// Responsive hooks
import { useResponsive } from '../hooks/UseResponsive';

// #endregion

// #region Interfaces

interface ResourceListProps {
  resourceSelected: (resource: GDFile) => void;
  selectedBreadcrumb: ResourceBreadcrumb;
  viewFile: (resource: GDFile) => void;
}

// #endregion

const ResourceList: React.FC<ResourceListProps> = (props) => {

  // Auth0 hooks
  const { getAccessToken } = useAuth0User();
  // Responsive hooks
  const { isMobile } = useResponsive();

  const [gdFiles, setGDFiles] = React.useState<Array<GDFile>>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  // #region Styles

  const theme = useTheme();
  const styles = {
    fileName: {
      padding: theme.spacing(0, 0, 0, 1)
    }
  };

  // #endregion

  // #region Methods

  /*
   * Conditional rendering of download icon based upon file type
   */
  const download = (params: GridCellParams): JSX.Element => {

    const fileType = params.row.fileType as GDFileType;
    if (fileType === GDFileType.PDF) {
      return (
        <>
        </>
      )
    } else {
      return <>
        {params.value as boolean ?
          <Tooltip title={`Download ${params.row.name}`}>
            <IconButton
              onClick={() => handleDownload(params.row.id as string, params.row.name as string)}
              size="large"><CloudDownload />
            </IconButton>
          </Tooltip>
          : null
        }
      </>;
    }

  }
  /*
 * Get folder content for a specified folder
 */
  const fetchFiles = async (id?: string) => {

    setIsLoading(true);

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Content-Type'] = 'application/json';

    const url = new URL(`${API_URL}/v1/resources`);

    // If file ID is passed
    if (id) {
      url.pathname += `/${id}`;
    }

    await fetch(url.toString(), {
      method: 'GET',
      cache: 'no-cache',
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        if (!response.ok) {
          setIsLoading(false);
          throw new Error('Root resources not retrieved');
        }
        return response.json()
      })
      .then((f: string) => {
        setGDFiles(JSON.parse(f) as Array<GDFile>);
        setIsLoading(false);
      })
      .catch(err => {
        throw new Error('Root resources not retrieved');
      });

  }

  /*
   * Is the file type a folder?
   */
  const isFolder = (fileType: GDFileType | string | null): boolean => {
    return (fileType === GDFileType.Folder);
  }

  /**
   * Transforms the raw modifiedTime value into a proper Date object.
   * This allows the Data Grid to sort and filter the column as a real Date.
   */
  const renderModifiedTime = (value: any, row: any): Date => {
    // 'value' corresponds to the data in the 'modifiedTime' field
    return new Date(value);
  };

  /*
   * Formats file name based upon file type
   */
  const renderFileName = (params: GridCellParams): JSX.Element => {

    const fileType = params.value as GDFileType;
    if (fileType === GDFileType.PDF) {
      return (
        <>
          <FileIcon fileType={params.value as GDFileType} />
          <Link component="button" underline="hover" sx={styles.fileName} onClick={() => handleFileView(params.row)}>{params.row.name}</Link>
        </>
      )
    } else {
      return (
        <>
          <FileIcon fileType={params.value as GDFileType} />
          <Link href="#" underline="hover" style={styles.fileName}>{params.row.name}</Link>
        </>
      )
    }
  }

  // #endregion

  // #region Event Handlers

  /*
   * Handle file  download
   */
  const handleDownload = async (id: string, fileName: string) => {

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    const url = new URL(`${API_URL}/v1/resources/download/${id}`);

    const params = new URLSearchParams();
    params.append('name', `${fileName}`);

    url.search = params.toString();

    await fetch(url.toString(), {
      method: 'GET'
      , cache: 'no-cache'
      , headers: headers
      , mode: 'cors'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('File was not downloaded');
        }
        return response.blob()
      })
      .then((f: Blob) => {
        saveAs(f, fileName);
      })
      .catch(err => {
        throw new Error('File was not downloaded');
      });

  }

  /**
   * Handles the file view action.
   * Directly accepts the GDFile type for better type safety.
   */
  const handleFileView = (row: GDFile) => {
    if (row.fileType === GDFileType.PDF) {
      props.viewFile(row);
    }
  };

  /*
   * Handles resource grid row click
   * Loads contents for a folder
   */
  const handleRowClick = async (params: GridRowParams) => {
    const row: GDFile = params.row as GDFile;
    if (row.fileType === GDFileType.Folder) {
      props.resourceSelected(params.row as GDFile);
      await fetchFiles(params.row.id as string);
    }
  }

  React.useEffect(() => {

    (async () => {
      // Select files based on a breadcumb selected
      if (props.selectedBreadcrumb && props.selectedBreadcrumb.id !== RESOURCE_BREADCRUMB_ROOT_ID) {
        await fetchFiles(props.selectedBreadcrumb.id);
      } else {
        await fetchFiles();
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedBreadcrumb]);

  // #endregion

  // #region Grid definition

  /*
   * Grid column definitions
   */
  const columns: Array<GridColDef> =
    [
      {
        field: 'fileType',
        headerName: 'Name',
        width: 400,
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridCellParams) => { return renderFileName(params); },
      },
      {
        field: 'modifiedTime',
        headerName: 'Last modified',
        width: 200,
        type: 'dateTime',
        valueGetter: renderModifiedTime
      },
      {
        field: 'size',
        headerName: 'File size',
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridCellParams) => (
          <>
            {isFolder(params.row.fileType) ? '-' : params.value}
          </>
        ),
        width: 125,
      },
      {
        field: 'canDownload',
        headerName: ' ',
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridCellParams) => { return download(params); },
        flex: 1
      },
    ];

  // #endregion

  return (
    <DataGrid
      rows={gdFiles}
      columns={columns}
      columnVisibilityModel={{
        modifiedTime: !isMobile,
        size: !isMobile,
      }}
      disableColumnMenu={true}
      disableColumnSelector
      disableRowSelectionOnClick
      hideFooter
      loading={isLoading}
      onRowClick={handleRowClick}
      sx={{
        height: 'auto', // Explicitly tell the Grid its height is auto
        '& .MuiDataGrid-main': { height: 'auto' } // Ensure the internal viewport follows suit
      }}
    />
  );
}

export default ResourceList;
